import { useState, useEffect, useRef } from "react";
import "./selectStoreTab.css";
import { useSelector } from "react-redux";

/*
  SelectStoreTab
  ──────────────
  Props:
    updateSelectedStore — fn({ storeId, storeName })
    resetFilter         — boolean  toggling this resets back to "All Stores"
    disableStoreFilter  — boolean  disables the selector (e.g. during custom range)

  Expects a stores list from your Redux store or API.
  Falls back to a hardcoded sample; replace `useStores()` with your real data hook.
*/

/* ── Replace this with your real stores from Redux / API ── */
function useStores() {
  return [
    { _id: "1", storeName: "All Stores" },
    /* Real stores come from your backend — add them here or fetch via useFetch */
  ];
}

export default function SelectStoreTab({ updateSelectedStore, resetFilter, disableStoreFilter }) {
  const { user } = useSelector((state) => state.webStoreUser);
  const stores = [{ _id: "1", storeName: "All Stores" }, ...user?.store];
  const [selected, setSelected] = useState({ storeId: "1", storeName: "All Stores" });
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  /* Reset to All Stores when parent toggles resetFilter */
  useEffect(() => {
    setSelected({ storeId: "1", storeName: "All Stores" });
  }, [resetFilter]);

  /* Close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (store) => {
    const val = { storeId: store._id, storeName: store.storeName };
    setSelected(val);
    updateSelectedStore(val);
    setOpen(false);
  };

  return (
    <div className='sst-wrap' ref={wrapRef}>
      <button
        className={`sst-btn${open ? " open" : ""}${disableStoreFilter ? " disabled" : ""}`}
        onClick={() => !disableStoreFilter && setOpen((p) => !p)}
        disabled={disableStoreFilter}
        aria-haspopup='listbox'
        aria-expanded={open}
      >
        <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' aria-hidden='true'>
          <path d='M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z' />
          <polyline points='9 22 9 12 15 12 15 22' />
        </svg>
        {selected.storeName}
        <svg
          width='12'
          height='12'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2.5'
          strokeLinecap='round'
          aria-hidden='true'
          style={{ transition: "transform 0.15s", transform: open ? "rotate(180deg)" : "none" }}
        >
          <polyline points='6 9 12 15 18 9' />
        </svg>
      </button>

      {open && (
        <ul className='sst-dropdown' role='listbox'>
          {stores.map((store) => (
            <li
              key={store._id}
              role='option'
              aria-selected={selected.storeId === store._id}
              className={`sst-option${selected.storeId === store._id ? " active" : ""}`}
              onClick={() => handleSelect(store)}
            >
              {selected.storeId === store._id && (
                <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' aria-hidden='true'>
                  <polyline points='20 6 9 17 4 12' />
                </svg>
              )}
              {store.storeName}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
